import { BrowserWindowEvent } from 'pf-frontend-common/src/module/browser/window.event';
import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { EventEmitterAwareInterface } from 'pf-frontend-common/src/module/event/emitter-aware.interface';
import { WindowServiceInterface } from 'pf-frontend-common/src/service/window/service.interface';

import { DataStoreEvent } from 'common/module/data/store.event';
import { TextTrimControlsViewStore } from 'common/module/text-trim/controls/view-store';
import { TextTrimViewEvent } from 'common/module/text-trim/view.event';
import { TextTrimViewOptionsInterface } from 'common/module/text-trim/view-options.interface';
import { ViewDynamicInterface } from 'common/module/view/dynamic.interface';

/**
 * Append expand and collapse buttons to a text container, then expand/collapse the container.
 */
export class TextTrimView implements EventEmitterAwareInterface {
  protected cssClass: DataKeyValueStringInterface = {
    contentExpanded: 'text-trim--expanded',
    textTrimEnabled: 'text-trim--enabled',
  };

  // Component Elements
  private element: HTMLElement;
  private elementControls: HTMLElement;

  // Callback function
  private callback: () => void;

  // Config
  private maxHeight: number;

  /**
   * Constructor
   */
  constructor(
    private windowService: WindowServiceInterface,
    private eventEmitter: EventEmitterInterface,
    private textTrimControlsViewDynamic: ViewDynamicInterface<TextTrimControlsViewStore>
  ) {}

  /**
   * @inheritdoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * @inheritdoc
   */
  public initialize(options: TextTrimViewOptionsInterface): void {
    this.element = options.element;
    this.elementControls = options.elementControls;
    this.callback = options.callback;
    this.maxHeight = Math.ceil(options.maxHeight ? options.maxHeight : this.element.getBoundingClientRect().height);

    this.initTextTrimControls(options);

    this.observers();
  }

  /**
   * @inheritdoc
   */
  public render(): void {
    requestAnimationFrame(() => {
      const controlsState = this.textTrimControlsViewDynamic.getViewStore().getState();

      // Don't load the component when the content isn't tall enough
      if (this.maxHeight >= this.element.scrollHeight) {
        if (controlsState.isVisible) {
          this.textTrimControlsViewDynamic.getViewStore().hide();
        }

        this.element.classList.toggle(this.cssClass.textTrimEnabled, false);
        this.element.classList.toggle(
          this.cssClass.contentExpanded,
          controlsState.isExpanded && controlsState.isVisible
        );

        return;
      }

      if (!controlsState.isVisible) {
        this.textTrimControlsViewDynamic.getViewStore().show();
      }

      this.element.classList.toggle(this.cssClass.textTrimEnabled, true);

      if (controlsState.isExpanded) {
        this.element.style.maxHeight = this.element.scrollHeight + 'px';

        this.element.classList.toggle(
          this.cssClass.contentExpanded,
          controlsState.isExpanded && controlsState.isVisible
        );

        return;
      }

      this.element.style.maxHeight = this.maxHeight + 'px';
      this.element.classList.toggle(this.cssClass.contentExpanded, controlsState.isExpanded && controlsState.isVisible);
    });
  }

  /**
   * Return the state of isExpanded
   */
  public getIsExpanded(): boolean {
    return this.textTrimControlsViewDynamic.getViewStore().getState().isExpanded;
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.windowService.getEventEmitter().addListener(BrowserWindowEvent.resize, this.onWindowResize);
  }

  /**
   * Initialize text trim controls
   */
  private initTextTrimControls(options: TextTrimViewOptionsInterface): void {
    let textTrimControlContainer;

    if (this.elementControls) {
      textTrimControlContainer = this.elementControls;
    } else {
      textTrimControlContainer = this.windowService.getNative().document.createElement('div');

      this.element.parentNode.insertBefore(textTrimControlContainer, this.element.nextSibling);
    }

    this.textTrimControlsViewDynamic.initialize({
      element: textTrimControlContainer,
    });

    this.textTrimControlsViewDynamic.getViewStore().initialize({
      isExpanded: false,
      isVisible: false,
      expandText: options.expandText,
      collapseText: options.collapseText,
      callback: this.callback,
      onClickLink: this.onClickLink,
    });

    this.textTrimControlsViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(DataStoreEvent.updateState, this.onUpdateStateTextTrimControls);
  }

  /**
   * Recalculate the full heights when the window is resized
   */
  private onWindowResize = (): void => {
    this.render();
  };

  /**
   * Clicked on text trim link
   */
  private onClickLink = (e: Event, isExpanded: boolean, callback?: () => void): void => {
    this.textTrimControlsViewDynamic.getViewStore().setExpanded(isExpanded);
    this.getEventEmitter().emit(isExpanded ? TextTrimViewEvent.expand : TextTrimViewEvent.collapse);
    if (callback && typeof callback === 'function') {
      callback();
    }
  };

  /**
   * Text trim controls state updated
   */
  private onUpdateStateTextTrimControls = (): void => {
    this.render();
  };
}
